import type { FC, FormEvent, ReactNode } from 'react'
import type { Agent } from '@/types'

import { useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { submitLead } from '@/lib/submit-lead'

import {
    FormError,
    FormSuccess,
    HoneypotField,
    SelectField,
    TextAreaField,
    TextField
} from '@/components/generals/form-fields'

interface ContactFormProps {
    agents: Agent[]
    agentId: string
    onAgentChange: (agentId: string) => void
}

export const ContactForm: FC<ContactFormProps> = ({ agents, agentId, onAgentChange }): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setStatus('sending')

        const result = await submitLead({
            type: 'contact',
            name,
            email,
            phone,
            message,
            agentId: agentId || undefined,
            website
        })

        if (result.ok) {
            setStatus('sent')

            return
        }

        setStatus('idle')
        setError(result.message)
    }

    if (status === 'sent') {
        return (
            <FormSuccess
                title='Message sent'
                onReset={() => {
                    setStatus('idle')
                    setMessage('')
                }}
            >
                Thanks for reaching out. We'll reply to <span>{email}</span> within one business day.
            </FormSuccess>
        )
    }

    return (
        <form onSubmit={handleSubmit} className='relative grid gap-5 sm:grid-cols-2'>
            <SelectField
                wrapperClassName='sm:col-span-2'
                label='Who would you like to talk to?'
                value={agentId}
                onChange={event => onAgentChange(event.target.value)}
            >
                <option value=''>Whoever is free first</option>
                {agents.map(agent => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
            </SelectField>

            <TextField label='Your name' value={name} onChange={event => setName(event.target.value)} required autoComplete='name' maxLength={100} />
            <TextField label='Email' type='email' value={email} onChange={event => setEmail(event.target.value)} required autoComplete='email' maxLength={254} />
            <TextField wrapperClassName='sm:col-span-2' label='Phone' type='tel' optional value={phone} onChange={event => setPhone(event.target.value)} autoComplete='tel' maxLength={40} />
            <TextAreaField
                wrapperClassName='sm:col-span-2'
                label='How can we help?'
                required
                value={message}
                onChange={event => setMessage(event.target.value)}
                maxLength={2000}
                placeholder='Buying, selling, or just getting a feel for the market...'
            />

            <HoneypotField value={website} onChange={setWebsite} />

            <div className='sm:col-span-2'>
                <FormError message={error} />
            </div>

            <div className='sm:col-span-2'>
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='bg-brand-gradient flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-white transition hover:brightness-105 disabled:opacity-70 sm:w-auto sm:px-8'
                >
                    {status === 'sending' && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
                    {status === 'sending' ? 'Sending...' : 'Send message'}
                </button>
            </div>
        </form>
    )
}
